import { FunctionComponent } from 'react';

import styled, { keyframes } from 'styled-components';

import media, { screenSizes } from 'chime-ui/system/media';

export const iconsPath =
  process.env.NODE_ENV === 'development' ? '/icons/' : '/pay/icons/';

// ============================================================================
// ANIMATIONS
// ============================================================================

export const rotate360 = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// ============================================================================
// GENERIC ICONS - UI and functional icons used throughout the application
// ============================================================================

export const ButtonSpinnerIcon = styled.img.attrs({
  'aria-label': 'Spinner Icon',
  src: `${iconsPath}loadingSpinner.png`,
})`
  width: 1.5rem;
  height: 1.5rem;
  animation: ${rotate360} 1s steps(8, end) infinite;
`;

export const CardBackIcon: FunctionComponent = () => (
  <CardBackStyling alt="Card Back Icon" src={`${iconsPath}cardBack.svg`} />
);

export const CardVendorIcon: FunctionComponent<{
  icon: string;
  vendor: string;
}> = ({ icon, vendor }) => {
  const altText = `${vendor} Icon`;
  const imgSrc = `${iconsPath}${icon}.svg`;

  return <CardVendorIconStyling alt={altText} src={imgSrc} />;
};

export const ChimeLogo: FunctionComponent = () => (
  <LogoStyling alt="Chime Logo" src={`${iconsPath}headerLogo.svg`} />
);

export const CloseIcon: FunctionComponent = () => (
  <CloseStyling alt="Close Modal" src={`${iconsPath}close.svg`} />
);

export const DebitCardNumber = styled.img.attrs({
  'aria-label': 'Debit Card Number',
  src: `${iconsPath}debitCardNumber.png`,
})`
  display: block;
  max-width: 278px;
`;

export const ErrorIcon: FunctionComponent = () => (
  <ErrorStyling alt="Error Icon" src={`${iconsPath}error.svg`} />
);

export const LightningBoltEmoji = styled.span.attrs(() => ({
  role: 'img',
  'aria-label': 'Lightning Bolt Emoji',
  children: '⚡',
}))``;

export const MicroBlinkIcon: FunctionComponent = () => (
  <img alt={'Powered by Microblink'} src={`${iconsPath}microblink.svg`} />
);

export const PayAnyonePhone = styled.img.attrs({
  'aria-label': 'Pay Anyone Phone',
  src: `${iconsPath}app_preview_pay_anyone.svg`,
})`
  display: block;
  height: auto;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  object-fit: contain;
`;

export const QuestionCircleIcon: FunctionComponent = () => (
  <QuestionCircleStyling
    alt="Question Circle Icon"
    src={`${iconsPath}questionCircle.svg`}
  />
);

export const WarningIcon = styled.span.attrs(() => ({
  role: 'img',
  'aria-label': 'Warning Emoji',
  children: '⚠️',
}))`
  margin-right: 0.5rem;
`;

export const WhiteCloseIcon: FunctionComponent = () => (
  <CloseStyling alt="Close Modal" src={`${iconsPath}white-close.svg`} />
);

// RFA success-screen 3D green check illustration (Figma flow 2+3 receipt). The mobile
// image has the warm RFA surface baked in; on the white desktop card (tablet width up)
// the transparent Figma export is used instead.
export const RfaSuccessCheck: FunctionComponent = () => (
  <picture>
    <source
      media={`(min-width: ${screenSizes.tablet}px)`}
      srcSet={`${iconsPath}rfa-success-check-desktop.png`}
    />
    <SuccessCheckImg alt="" src={`${iconsPath}rfa-success-check.png`} />
  </picture>
);

// ============================================================================
// VALUE PROP ICONS - Marketing and feature highlight icons (Alphabetical)
// ============================================================================

export const BadgeCheckMarkIcon: FunctionComponent = () => (
  <ValuePropIcon
    alt="Check Mark Badge"
    src={`${iconsPath}badge.check_mark.svg`}
  />
);

export const BadgeDirectDepositIcon: FunctionComponent = () => (
  <ValuePropIcon
    alt="Direct Deposit Badge"
    src={`${iconsPath}badge.direct_deposit.svg`}
  />
);

export const BadgeDollarSignIcon: FunctionComponent = () => (
  <BadgeDollarSignStyling
    alt="Dollar Sign Badge"
    src={`${iconsPath}badge.dollar_sign.svg`}
  />
);

export const BadgeNoFeesIcon: FunctionComponent = () => (
  <ValuePropIcon alt="No Fees Badge" src={`${iconsPath}badge.no_fees.svg`} />
);

export const BadgePayAnyoneIcon: FunctionComponent = () => (
  <ValuePropIcon
    alt="Pay Anyone Badge"
    src={`${iconsPath}badge.pay_anyone.svg`}
  />
);

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ValuePropIcon = styled.img`
  width: 100%;
`;

const SuccessCheckImg = styled.img`
  display: block;
  width: 88px;
  height: auto;
`;

const BadgeDollarSignStyling = styled.img`
  width: 80px;
`;

const StyledIcon = styled.img`
  display: block;
  margin: 1.5rem auto 0.75rem;
  width: 86px;
  height: auto;
`;

const LogoStyling = styled(StyledIcon)`
  margin: 0;
`;

const CloseStyling = styled(StyledIcon)`
  margin: 0;
  width: 24px;
`;

const ErrorStyling = styled(StyledIcon)`
  margin: 0 0 1.5rem;
  width: 44px;
`;

const QuestionCircleStyling = styled(StyledIcon)`
  margin: 0;
  width: 1rem;
`;

const CardBackStyling = styled(StyledIcon)`
  margin: 0;
  width: auto;
  ${media.tablet`
    margin: 0 8.5rem;
  `}
`;

const CardVendorIconStyling = styled(StyledIcon)`
  margin: 0;
  width: 2.125rem;
  margin-right: 0.5rem;
`;
